import { ArrowRight } from 'lucide-react';
import { brands, DOZZ_LOGO_URL, DOZZ_GOLD_LOGO_URL, VIDYUTSAFE_LOGO_URL } from '../../data/mock';
import { useInView } from '../../hooks/useInView';

// Map brand names to their actual logo URLs
const brandLogos = {
  'DOZZ': DOZZ_LOGO_URL,
  'DOZZ GOLD': DOZZ_GOLD_LOGO_URL,
  'VIDYUTSAFE': VIDYUTSAFE_LOGO_URL,
};

export const BrandsSection = () => {
  const [ref, isInView] = useInView();

  return (
    <section id="brands" className="py-20 lg:py-28 bg-white" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className={`text-center mb-14 ${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}>
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="section-divider" />
            <span className="text-sm font-semibold text-ind-red-600 uppercase tracking-wider">Our Brands</span>
            <div className="section-divider" />
          </div>
          <h2 className="font-heading font-extrabold text-navy-950 text-3xl lg:text-4xl mb-3">
            Trusted Brand Portfolio
          </h2>
          <p className="text-navy-500 text-base max-w-2xl mx-auto">
            Five specialized brands covering the complete spectrum of industrial hose and electrical safety solutions.
          </p>
        </div>

        {/* Brands Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {brands.map((brand, idx) => (
            <div
              key={brand.name}
              className={`group relative bg-white rounded-2xl border border-navy-100 overflow-hidden ${
                isInView ? 'animate-fade-in-up' : 'opacity-0'
              } ${idx === 0 ? 'sm:col-span-2 lg:col-span-2' : ''}`}
              style={{ animationDelay: `${idx * 0.1}s` }}
            >
              {/* Top accent bar */}
              <div className="h-1.5 w-full" style={{ backgroundColor: brand.color }} />

              <div className="p-6 lg:p-8">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-4">
                    {/* Show actual logo if available, otherwise show initial */}
                    {brandLogos[brand.name] ? (
                      <div className="w-14 h-14 rounded-xl overflow-hidden shadow-md bg-white flex items-center justify-center">
                        <img 
                          src={brandLogos[brand.name]} 
                          alt={`${brand.name} Logo`} 
                          className="w-full h-full object-contain"
                        />
                      </div>
                    ) : (
                      <div
                        className="w-14 h-14 rounded-xl flex items-center justify-center text-white font-heading font-extrabold text-xl shadow-md"
                        style={{ backgroundColor: brand.color }}
                      >
                        {brand.name.charAt(0)}{brand.name.includes(' ') ? brand.name.split(' ')[1]?.charAt(0) : ''}
                      </div>
                    )}
                    <div>
                      <h3 className="font-heading font-bold text-navy-950 text-xl">{brand.name}</h3>
                      <p className="text-sm text-navy-500">{brand.tagline}</p>
                    </div>
                  </div>
                </div>
                <p className="text-navy-600 text-sm leading-relaxed">{brand.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
